import { useEffect, useState, useMemo } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { customersApi, businessTypesApi, profileApi } from '../../../services/api'
import ExportBar, { ExportColumn } from '../../../components/ExportBar'
import TableActionButton from '../../../components/TableActionButton'
import { useSortableData } from '../../../hooks/useSortableData'
import SortableTh from '../../../components/SortableTh'
import CustomerFormModal from './CustomerFormModal'
import PricingModal from './PricingModal'
import { type BusinessType, type Customer, type CustomerForm, emptyCustomerForm } from './types'

const ROWS_PER_PAGE = 100

export default function WholesaleTab() {
  const { user } = useAuth()
  const [allCustomers, setAllCustomers] = useState<Customer[]>([])
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<CustomerForm>(emptyCustomerForm('wholesale'))
  const [showLocationPicker, setShowLocationPicker] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [filterDistrict, setFilterDistrict] = useState('')
  const [filterSellerId, setFilterSellerId] = useState('')
  const [page, setPage] = useState(1)
  const [canEdit, setCanEdit] = useState(user?.role === 'admin')
  const [pricingFor, setPricingFor] = useState<Customer | null>(null)

  const isAdmin = user?.role === 'admin'

  const fetchCustomers = async () => {
    try {
      const res = await customersApi.list()
      setAllCustomers(res.data.customers || [])
    } catch (err) {
      console.error('Error fetching customers:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchBusinessTypes = async () => {
    try {
      const res = await businessTypesApi.list()
      setBusinessTypes(res.data.business_types || [])
    } catch (err) {
      console.error('Error fetching business types:', err)
    }
  }

  useEffect(() => {
    fetchCustomers()
    fetchBusinessTypes()
    if (!isAdmin) {
      profileApi.get().then((res) => setCanEdit(res.data.permissions?.edit_customers !== false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const customers = useMemo(() => allCustomers.filter((c) => c.channel === 'wholesale'), [allCustomers])

  const otroBusinessTypeId = useMemo(
    () => businessTypes.find((bt) => bt.name.toLowerCase() === 'otro')?.id ?? null,
    [businessTypes]
  )

  // Wholesale accounts are bodegas by definition -- the fuller street-side
  // taxonomy (Restaurante, Grifo, Juguería, etc.) doesn't apply here, so the
  // form only offers "Bodega" and the "Otro" escape hatch. If an existing
  // customer already has some other business type (e.g. switched to this
  // channel after being created as street), keep it in the options list
  // instead of silently hiding/discarding it while editing.
  const wholesaleBusinessTypes = useMemo(() => {
    const allowed = businessTypes.filter((bt) => ['bodega', 'otro'].includes(bt.name.toLowerCase()))
    if (form.business_type_id != null && !allowed.some((bt) => bt.id === form.business_type_id)) {
      const current = businessTypes.find((bt) => bt.id === form.business_type_id)
      if (current) return [...allowed, current]
    }
    return allowed
  }, [businessTypes, form.business_type_id])

  const districts = useMemo(() => {
    const set = new Set<string>()
    customers.forEach((c) => {
      if (c.district) set.add(c.district)
    })
    return Array.from(set).sort()
  }, [customers])

  const sellers = useMemo(() => {
    const map = new Map<number, string>()
    customers.forEach((c) => {
      if (c.seller_id != null) map.set(c.seller_id, c.seller_name)
    })
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [customers])

  const exportColumns = useMemo<ExportColumn<Customer>[]>(() => {
    const base: ExportColumn<Customer>[] = [
      { key: 'id', label: 'ID', format: (c) => `#${c.id}` },
      { key: 'name', label: 'Nombre', format: (c) => `${c.name}${c.nickname ? ` (${c.nickname})` : ''}` },
      { key: 'district', label: 'Distrito', format: (c) => c.district || '-' },
      { key: 'address', label: 'Dirección', format: (c) => c.address || '-' },
      { key: 'phone', label: 'Teléfono', format: (c) => c.phone || '-' },
      { key: 'ruc', label: 'RUC', format: (c) => c.ruc || '-' },
      { key: 'created_at', label: 'Añadido', format: (c) => new Date(c.created_at).toLocaleDateString() },
    ]
    if (isAdmin) {
      base.push({ key: 'seller_name', label: 'Vendedor', format: (c) => c.seller_name })
    }
    return base
  }, [isAdmin])

  const filtered = useMemo(() => {
    return customers.filter((c) => {
      if (filterDistrict) {
        const q = filterDistrict.toLowerCase()
        if (!(c.district || '').toLowerCase().includes(q)) return false
      }
      if (filterSellerId) {
        if (String(c.seller_id) !== filterSellerId) return false
      }
      return true
    })
  }, [customers, filterDistrict, filterSellerId])

  const { sortedData: sortedFiltered, sortConfig, requestSort } = useSortableData(filtered, {
    key: 'created_at',
    direction: 'desc',
  })

  const totalPages = Math.ceil(sortedFiltered.length / ROWS_PER_PAGE)
  const paged = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE
    return sortedFiltered.slice(start, start + ROWS_PER_PAGE)
  }, [sortedFiltered, page])

  const clearFilters = () => {
    setFilterDistrict('')
    setFilterSellerId('')
    setPage(1)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: name === 'business_type_id' ? (value === '' ? null : Number(value)) : value,
    }))
  }

  const openNew = () => {
    setForm(emptyCustomerForm('wholesale'))
    setEditingId(null)
    setError('')
    setShowLocationPicker(false)
    setShowForm(true)
  }

  const openEdit = (c: Customer) => {
    setForm({
      name: c.name,
      nickname: c.nickname || '',
      address: c.address || '',
      district: c.district || '',
      phone: c.phone || '',
      dni: c.dni || '',
      ruc: c.ruc || '',
      payment_mode: c.payment_mode,
      cycle_days: c.cycle_days || 30,
      business_type_id: c.business_type_id || null,
      business_type_other: c.business_type_other || '',
      channel: c.channel || 'wholesale',
      details: c.details || '',
      latitude: c.latitude ?? null,
      longitude: c.longitude ?? null,
    })
    setEditingId(c.id)
    setError('')
    setShowLocationPicker(c.latitude != null && c.longitude != null)
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    if (form.business_type_id === otroBusinessTypeId && !form.business_type_other.trim()) {
      setError('Por favor especifica el tipo de negocio')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        ...form,
        nickname: form.nickname || null,
        address: form.address || null,
        district: form.district || null,
        phone: form.phone || null,
        dni: form.dni || null,
        ruc: form.ruc || null,
        cycle_days: form.cycle_days || 30,
        business_type_id: form.business_type_id || null,
        business_type_other: form.business_type_id === otroBusinessTypeId ? form.business_type_other.trim() : null,
        details: form.details.trim() || null,
        latitude: form.latitude,
        longitude: form.longitude,
      }
      if (editingId) {
        await customersApi.update(editingId, payload)
      } else {
        await customersApi.create(payload)
      }
      setShowForm(false)
      fetchCustomers()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar cliente')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar esta cuenta mayorista?')) return
    try {
      await customersApi.delete(id)
      fetchCustomers()
    } catch (err) {
      console.error('Error deleting customer:', err)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-espresso-500">
          Bodegas y tiendas de consumo masivo. Cada cuenta necesita al menos un precio pactado antes de poder venderle.
        </p>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium whitespace-nowrap"
        >
          + Agregar Cuenta Mayorista
        </button>
      </div>

      {showForm && (
        <CustomerFormModal
          editingId={editingId}
          form={form}
          businessTypes={wholesaleBusinessTypes}
          otroBusinessTypeId={otroBusinessTypeId}
          error={error}
          saving={saving}
          showLocationPicker={showLocationPicker}
          onShowLocationPicker={() => setShowLocationPicker(true)}
          onChange={handleChange}
          onLocationChange={(lat, lng) => setForm({ ...form, latitude: lat, longitude: lng })}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}

      {pricingFor && <PricingModal customer={pricingFor} onClose={() => setPricingFor(null)} />}

      {customers.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay cuentas mayoristas aún. Agrega la primera para comenzar.</p>
        </div>
      ) : (
        <>
          <div className="bg-cream-50 shadow rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-espresso-700">Filtros</h3>
              <button onClick={clearFilters} className="text-xs text-terracotta-700 hover:text-terracotta-800">Limpiar todo</button>
            </div>
            <div className={`grid grid-cols-2 gap-3 ${isAdmin ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
              <div>
                <label className="block text-xs font-medium text-espresso-500 mb-1">Distrito</label>
                <select value={filterDistrict} onChange={(e) => { setFilterDistrict(e.target.value); setPage(1) }}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
                  <option value="">Todos</option>
                  {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              {isAdmin && (
                <div>
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Vendedor</label>
                  <select value={filterSellerId} onChange={(e) => { setFilterSellerId(e.target.value); setPage(1) }}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
                    <option value="">Todos</option>
                    {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mb-3 text-sm text-espresso-600">
            <span>{filtered.length} cuenta{filtered.length !== 1 ? 's' : ''} mayorista{filtered.length !== 1 ? 's' : ''}</span>
            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                  className="px-3 py-1 border border-espresso-300 rounded-md text-sm hover:bg-cream-100 disabled:opacity-50">Anterior</button>
                <span>Página {page} de {totalPages}</span>
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                  className="px-3 py-1 border border-espresso-300 rounded-md text-sm hover:bg-cream-100 disabled:opacity-50">Siguiente</button>
              </div>
            )}
          </div>

          <ExportBar data={filtered} columns={exportColumns} filename="clientes_mayoristas" title="Cuentas Mayoristas" />

          <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-espresso-100">
                <thead className="bg-cream-50">
                  <tr>
                    <SortableTh label="ID" sortKey="id" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Nombre" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Distrito" sortKey="district" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Dirección" sortKey="address" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Teléfono" sortKey="phone" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="RUC" sortKey="ruc" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Añadido" sortKey="created_at" sortConfig={sortConfig} onSort={requestSort} />
                    {isAdmin && (
                      <SortableTh label="Vendedor" sortKey="seller_name" sortConfig={sortConfig} onSort={requestSort} />
                    )}
                    <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-cream-50 divide-y divide-espresso-100">
                  {paged.map((c) => (
                    <tr key={c.id} className="hover:bg-cream-100">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">#{c.id}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-espresso-800">{c.name}</div>
                        {c.nickname && <div className="text-xs text-espresso-500">{c.nickname}</div>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                        {c.district || '-'}
                      </td>
                      <td
                        className="px-4 py-4 max-w-[220px] truncate text-sm text-espresso-800"
                        title={c.address || undefined}
                      >
                        {c.address || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                        {c.phone || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                        {c.ruc || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                          {c.seller_name}
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                        {canEdit ? (
                          <div className="flex items-center justify-end gap-1">
                            <TableActionButton onClick={() => setPricingFor(c)} variant="primary">
                              Precios
                            </TableActionButton>
                            <TableActionButton onClick={() => openEdit(c)} variant="primary">
                              Editar
                            </TableActionButton>
                            <TableActionButton onClick={() => handleDelete(c.id)} variant="danger">
                              Eliminar
                            </TableActionButton>
                          </div>
                        ) : (
                          <span className="text-espresso-400 text-xs">Sin permiso</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {paged.length === 0 && (
              <div className="text-center py-8 text-espresso-500">No hay cuentas que coincidan con los filtros.</div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-4 space-x-2 text-sm">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-100 disabled:opacity-50">Primero</button>
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-100 disabled:opacity-50">Anterior</button>
              <span className="text-espresso-600">Página {page} de {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-100 disabled:opacity-50">Siguiente</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-100 disabled:opacity-50">Último</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
